import { html, nothing, svg, type TemplateResult } from "lit";
import {
  footprintBounds,
  isSpotSize,
  spotFootprint,
  type Bounds,
  type Footprint,
} from "../../../generated/room-geometry";
import type { RoomAvailability, SpotAvailability } from "../../../data/live";
import { SEAT_COLS, SEAT_ROWS, TAKEN_SEATS } from "../../data/mock-pricing";
import type { Attendee } from "./booking-checkout.model";

export interface BookingSeatsViewModel {
  attendees: readonly { attendee: Attendee; label: string }[];
  activeAttendee: number;
  error: string | null;
  roomAvailability: RoomAvailability | null;
  nextAction: TemplateResult;
  onSelectAttendee: (index: number) => void;
  onAssignGenericSeat: (seat: string) => void;
  onSelectRealSpot: (spot: SpotAvailability) => void;
}

export function renderBookingSeats(
  model: BookingSeatsViewModel,
): TemplateResult {
  const selectedSeats = new Set(
    model.attendees.map(({ attendee }) => attendee.seat).filter(Boolean),
  );
  const roomMap = model.roomAvailability
    ? renderRoomMap(
        model.roomAvailability,
        selectedSeats,
        model.onSelectRealSpot,
      )
    : null;
  return html`
    <div class="chips">
      ${model.attendees.map(
        ({ attendee, label }, index) =>
          html`<button
            class="seat-chip"
            aria-pressed=${index === model.activeAttendee}
            @click=${() => model.onSelectAttendee(index)}
          >
            <span class="n">${index + 1}</span> ${label}
            <span class="seatval">${attendee.seat || "Pick a spot"}</span>
          </button>`,
      )}
    </div>
    ${roomMap ? nothing : html`<div class="front">Front of room</div>`}
    ${model.error ? html`<div class="err">${model.error}</div>` : nothing}
    ${roomMap ??
    (model.roomAvailability
      ? renderRealSeatGrid(
          model.roomAvailability,
          selectedSeats,
          model.onSelectRealSpot,
        )
      : renderGenericSeatGrid(
          selectedSeats,
          model.attendees,
          model.onAssignGenericSeat,
        ))}
    <div class="legend">
      <span><span class="sw"></span> Available</span>
      <span><span class="sw mine"></span> Your spots</span>
      <span><span class="sw taken"></span> Taken</span>
    </div>
    ${model.nextAction}
  `;
}

function renderGenericSeatGrid(
  selectedSeats: ReadonlySet<string>,
  attendees: readonly { attendee: Attendee; label: string }[],
  onAssign: (seat: string) => void,
): TemplateResult {
  const ownerNumber = (seat: string) =>
    attendees.findIndex(({ attendee }) => attendee.seat === seat) + 1;
  return html`<div class="seat-rows">
    ${SEAT_ROWS.map(
      (row) =>
        html`<div class="seat-row">
          <span class="rl">${row}</span>
          <div class="seat-cells">
            ${Array.from({ length: SEAT_COLS }, (_, column) => {
              const seat = `${row}${column + 1}`;
              const taken = TAKEN_SEATS.has(seat);
              const selected = selectedSeats.has(seat);
              return html`<button
                class="seat ${taken ? "taken" : ""} ${selected ? "mine" : ""}"
                ?disabled=${taken}
                @click=${() => onAssign(seat)}
              >
                ${selected ? ownerNumber(seat) : seat}
              </button>`;
            })}
          </div>
        </div>`,
    )}
  </div>`;
}

function renderRealSeatGrid(
  room: RoomAvailability,
  selectedSeats: ReadonlySet<string>,
  onSelect: (spot: SpotAvailability) => void,
): TemplateResult {
  const columns = Math.max(1, room.gridWidth || 6);
  const spots = [...room.spots].sort(
    (left, right) => left.ordinal - right.ordinal,
  );
  return html`<div
    class="seat-cells"
    style=${`grid-template-columns:repeat(${columns},1fr);max-width:360px`}
  >
    ${spots.map((spot) => {
      const open = spot.state === "available" || spot.state === "held-by-me";
      const selected = selectedSeats.has(spot.label);
      return html`<button
        class="seat ${open ? "" : "taken"} ${selected ? "mine" : ""}"
        title=${spot.state}
        ?disabled=${!open && !selected}
        @click=${() => onSelect(spot)}
      >
        ${spot.label}
      </button>`;
    })}
  </div>`;
}

/** Spots and obstructions drawn as one shape, rotated about their center. */
function renderShape(footprint: Footprint): TemplateResult {
  const center = footprint.center!;
  return svg`<g
    transform=${`translate(${center.x} ${center.y}) rotate(${footprint.angle})`}
  >
    ${footprint.shape === "circle"
      ? svg`<circle class="shape" r=${footprint.width / 2}></circle>`
      : svg`<rect
          class="shape"
          x=${-footprint.width / 2}
          y=${-footprint.height / 2}
          width=${footprint.width}
          height=${footprint.height}
          rx=${footprint.shape === "square" ? 3 : 5}
        ></rect>`}
  </g>`;
}

/** Splits a long label at the space nearest its middle, else keeps one line. */
function labelLines(label: string): string[] {
  if (label.length <= 10) return [label];
  const middle = label.length / 2;
  let split = -1;
  for (let index = 0; index < label.length; index++) {
    if (
      label[index] === " " &&
      (split < 0 || Math.abs(index - middle) < Math.abs(split - middle))
    )
      split = index;
  }
  return split < 0
    ? [label]
    : [label.slice(0, split).trim(), label.slice(split + 1).trim()];
}

/** Labels stay upright and shrink so long names fit inside the shape. */
function renderLabel(footprint: Footprint, label: string): TemplateResult {
  const center = footprint.center!;
  const lines = labelLines(label);
  const longest = Math.max(4, ...lines.map((line) => line.length));
  const fontSize = Math.min(
    24,
    (footprint.width * 1.7) / longest,
    (footprint.height * 0.8) / lines.length,
  );
  const top = center.y - ((lines.length - 1) * fontSize * 1.1) / 2;
  return svg`<text
    x=${center.x}
    font-size=${fontSize}
    text-anchor="middle"
    dominant-baseline="central"
  >${lines.map(
    (line, index) =>
      svg`<tspan x=${center.x} y=${top + index * fontSize * 1.1}>${line}</tspan>`,
  )}</text>`;
}

/** Frames what was placed; the saved bounds include the designer's empty canvas. */
function fitBounds(footprints: Footprint[], fallback: Bounds): Bounds {
  const boxes = footprints.flatMap((footprint) => {
    const box = footprintBounds(footprint);
    return box ? [box] : [];
  });
  if (!boxes.length) return fallback;
  const pad = 24;
  const left = Math.min(...boxes.map((box) => box.x)) - pad;
  const top = Math.min(...boxes.map((box) => box.y)) - pad;
  const right = Math.max(...boxes.map((box) => box.x + box.width)) + pad;
  const bottom = Math.max(...boxes.map((box) => box.y + box.height)) + pad;
  return { x: left, y: top, width: right - left, height: bottom - top };
}

/**
 * The room as the studio designed it. Returns null when the room has no
 * placed spots, so the caller keeps the plain grid.
 */
export function renderRoomMap(
  room: RoomAvailability,
  selectedSeats: ReadonlySet<string>,
  onSelect: (spot: SpotAvailability) => void,
): TemplateResult | null {
  const layout = room.layout;
  if (!layout) return null;
  const size = isSpotSize(layout.spotSize) ? layout.spotSize : null;
  const geometryById = new Map(
    layout.spots.map((spot) => [spot.id, spot.geometry]),
  );
  const ordered = [...room.spots].sort(
    (left, right) => left.ordinal - right.ordinal,
  );
  const placed: { spot: SpotAvailability; footprint: Footprint }[] = [];
  const unplaced: SpotAvailability[] = [];
  for (const spot of ordered) {
    const geometry = geometryById.get(spot.spotId) ?? spot.geometry;
    if (geometry?.center) {
      placed.push({ spot, footprint: spotFootprint(geometry, size) });
    } else {
      unplaced.push(spot);
    }
  }
  if (!placed.length) return null;
  const { x, y, width, height } = fitBounds(
    [...placed.map(({ footprint }) => footprint), ...layout.obstructions],
    layout.bounds,
  );
  const isOpen = (spot: SpotAvailability) =>
    spot.state === "available" || spot.state === "held-by-me";
  return html`<div class="room-map">
    <svg
      viewBox=${`${x} ${y} ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      role="group"
      aria-label="Room layout"
    >
      ${layout.obstructions
        .filter((item) => item.center)
        .map(
          (item) => svg`<g class="room-obstruction" aria-hidden="true">
            ${renderShape(item)}${renderLabel(item, item.label)}
          </g>`,
        )}
      ${placed.map(({ spot, footprint }) => {
        const open = isOpen(spot);
        const selected = selectedSeats.has(spot.label);
        const choosable = open || selected;
        const choose = () => {
          if (choosable) onSelect(spot);
        };
        return svg`<g
          class="room-spot ${open ? "" : "taken"} ${selected ? "mine" : ""}"
          role="button"
          tabindex=${choosable ? 0 : -1}
          aria-label=${`${spot.label}${open || selected ? "" : ", taken"}`}
          aria-pressed=${selected ? "true" : "false"}
          aria-disabled=${choosable ? "false" : "true"}
          @click=${choose}
          @keydown=${(event: KeyboardEvent) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            choose();
          }}
        >
          <title>${spot.label}</title>
          ${renderShape(footprint)}${renderLabel(footprint, spot.label)}
        </g>`;
      })}
    </svg>
    ${unplaced.length
      ? html`<div class="room-unplaced-title">More spots</div>
          <div
            class="seat-cells"
            style="grid-template-columns:repeat(6,1fr);max-width:360px"
          >
            ${unplaced.map((spot) => {
              const open = isOpen(spot);
              const selected = selectedSeats.has(spot.label);
              return html`<button
                class="seat ${open ? "" : "taken"} ${selected ? "mine" : ""}"
                title=${spot.state}
                ?disabled=${!open && !selected}
                @click=${() => onSelect(spot)}
              >
                ${spot.label}
              </button>`;
            })}
          </div>`
      : nothing}
  </div>`;
}
